import React, { useState, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
} from '@mui/material';
import { CameraAlt, Close, CheckCircle, Upload } from '@mui/icons-material';
import Webcam from 'react-webcam';
import { api } from '../api';
// Tesseract removed - using server-side Google Vision API

function PassportScanner({ 
  open, 
  onClose, 
  onScanComplete, 
  mode = "camera",
  maxWidth = "md",
  maxHeight = "90vh"
}) {
  const webcamRef = useRef(null);
  const fileInputRef = useRef(null);
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  // activeTab removed - using mode prop instead
  const [cameraAvailable, setCameraAvailable] = useState(false);
  const [lastCapturedImage, setLastCapturedImage] = useState(null);
  const [showDebugImage, setShowDebugImage] = useState(false);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [cameraInitialized, setCameraInitialized] = useState(false);
  // Image rotation state removed - not needed anymore

  // אתחול המצלמה כשהדיאלוג נפתח
  React.useEffect(() => {
    if (open && mode === "camera") {
      console.log('🎥 Dialog opened - initializing camera...');
      setCameraInitialized(false);
      setCameraAvailable(false);
      setError(null);
      setIsScanning(false);
      setSuccess(false);
      
      // איפוס מצב המצלמה כשהדיאלוג נפתח
      setTimeout(() => {
        setCameraInitialized(true);
      }, 100);
    } else if (!open) {
      // איפוס כל ה-states כשהדיאלוג נסגר
      console.log('🚪 Dialog closed - resetting states...');
      setCameraInitialized(false);
      setCameraAvailable(false);
      setError(null);
      setIsScanning(false);
      setSuccess(false);
      setLastCapturedImage(null);
      setUploadedImage(null);
    }
  }, [open, mode]);

  const capture = useCallback(async () => {
    if (!webcamRef.current) {
      setError('מצלמה לא זמינה - אנא רענן את הדף ונסה שוב');
      return;
    }
    
    if (!cameraAvailable) {
      setError('מצלמה עדיין לא מוכנה - אנא המתן רגע ונסה שוב');
      return;
    }
    
    setIsScanning(true);
    setError(null);
    setSuccess(false);

    try {
      // צילום התמונה ברזולוציה גבוהה
      const imageSrc = webcamRef.current.getScreenshot({
        width: 1920,
        height: 1440,
        quality: 1.0
      });
      
      if (!imageSrc) {
        throw new Error('לא ניתן לצלם תמונה');
      }

      console.log('📸 תמונה נצלמה בהצלחה, מתחיל עיבוד OCR...');
      
      // בדיקת איכות התמונה
      const img = new Image();
      img.onload = () => {
        console.log('🖼️ פרטי התמונה:', {
          width: img.width,
          height: img.height,
          size: Math.round(imageSrc.length / 1024) + ' KB',
          format: imageSrc.substring(0, 30) + '...'
        });
      };
      img.src = imageSrc;
      
      // שמירת התמונה לדיבוג
      setLastCapturedImage(imageSrc);

      // שימוש בפונקציה המשותפת לעיבוד OCR
      await processImageOCR(imageSrc);

    } catch (err) {
      console.error('שגיאה בסריקת הדרכון:', err);
      setError('שגיאה בסריקת הדרכון. אנא נסה שוב.');
    } finally {
      setIsScanning(false);
    }
  }, [onScanComplete, onClose]);

  const handleFileUpload = useCallback(async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsScanning(true);
    setError(null);
    setSuccess(false);

    try {
      console.log('🧹 מנקה נתונים קודמים לפני סריקה חדשה...');
      
      // המרת הקובץ ל-Data URL
      const reader = new FileReader();
      reader.onload = async (e) => {
        const imageSrc = e.target.result;
        setUploadedImage(imageSrc);
        setLastCapturedImage(imageSrc);

        console.log('📁 קובץ הועלה בהצלחה, מתחיל עיבוד OCR...');

        // שימוש באותו אלגוריתם OCR
        await processImageOCR(imageSrc);
      };
      reader.readAsDataURL(file);

    } catch (err) {
      console.error('שגיאה בהעלאת הקובץ:', err);
      setError('שגיאה בהעלאת הקובץ. אנא נסה שוב.');
      setIsScanning(false);
    }
  }, [onScanComplete, onClose]);

  // Image rotation removed - server handles all image processing

  // פונקציה לדחיסת תמונה
  const compressImage = (imageSrc, maxSizeMB = 5, quality = 0.8) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // חישוב גודל חדש (מקסימום 1920x1920)
        const maxDimension = 1920;
        let { width, height } = img;
        
        if (width > height) {
          if (width > maxDimension) {
            height = (height * maxDimension) / width;
            width = maxDimension;
          }
        } else {
          if (height > maxDimension) {
            width = (width * maxDimension) / height;
            height = maxDimension;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        
        // ציור התמונה בגודל החדש
        ctx.drawImage(img, 0, 0, width, height);
        
        // המרה ל-base64 עם דחיסה
        const compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
        
        console.log('🗜️ דחיסת תמונה:', {
          originalSize: Math.round(imageSrc.length / 1024) + ' KB',
          compressedSize: Math.round(compressedDataUrl.length / 1024) + ' KB',
          originalDimensions: `${img.width}x${img.height}`,
          newDimensions: `${width}x${height}`
        });
        
        resolve(compressedDataUrl);
      };
      img.src = imageSrc;
    });
  };

  const processImageOCR = async (imageSrc) => {
    try {
      // בדיקת גודל התמונה ודחיסה במידת הצורך
      const imageSizeKB = imageSrc.length / 1024;
      console.log('📏 גודל התמונה המקורית:', Math.round(imageSizeKB) + ' KB');
      
      let processedImage = imageSrc;
      
      // אם התמונה גדולה מ-5MB, דחס אותה
      if (imageSizeKB > 5 * 1024) {
        console.log('🗜️ התמונה גדולה מדי, מדחיס...');
        processedImage = await compressImage(imageSrc, 5, 0.8);
      }

      // בדיקת איכות התמונה
      const img = new Image();
      await new Promise((resolve) => {
        img.onload = () => {
          const aspectRatio = img.width / img.height;
          console.log('🖼️ פרטי התמונה:', {
            width: img.width,
            height: img.height,
            aspectRatio: aspectRatio.toFixed(2),
            orientation: aspectRatio > 1 ? 'לרוחב' : 'לאורך',
            size: Math.round(processedImage.length / 1024) + ' KB'
          });
          resolve();
        };
        img.src = processedImage;
      });

      // שליחה לשרת לעיבוד OCR עם Google Vision API
      console.log('🔍 שולח תמונה לשרת לעיבוד OCR עם Google Vision...');
      console.log('📝 גודל התמונה שנשלחת:', processedImage?.length || 'undefined');
      console.log('📝 התחלת התמונה:', processedImage?.substring(0, 50) || 'undefined');
      
      const response = await api.post('/ocr/passport', {
        imageBase64: processedImage
      });

      const result = response.data;
      console.log('📡 תגובה מהשרת:', result);
      
      // הוסף logging לטקסט המלא אם זמין
      if (result.success && result.data && result.data.rawText) {
        console.log('📝 Full OCR text detected:');
        console.log('='.repeat(50));
        console.log(result.data.rawText);
        console.log('='.repeat(50));
      }

      if (result.success && result.data) {
        const extractedData = result.data;
        console.log('✅ Google Vision OCR הושלם בהצלחה!');
        
        // Format the data for all fields (matching form field names)
        const formattedData = {
          passportNumber: extractedData.passportNumber || '',
          firstNameEn: extractedData.firstNameEn || '',
          lastNameEn: extractedData.lastNameEn || '',
          birthDate: extractedData.birthDate ? new Date(extractedData.birthDate) : null,
          // Auto-fill citizenship based on passport detection
          citizenship: extractedData.isIsraeliPassport ? 'ישראלי' : 'אחר',
          // Add Israeli ID if detected
          ...(extractedData.isIsraeliPassport && extractedData.idNumber && {
            idNumber: extractedData.idNumber
          })
        };
        
        console.log('📊 נתונים מעוצבים:', {
          passportNumber: formattedData.passportNumber || 'לא נמצא',
          firstNameEn: formattedData.firstNameEn || 'לא נמצא', 
          lastNameEn: formattedData.lastNameEn || 'לא נמצא',
          birthDate: formattedData.birthDate ? formattedData.birthDate.toLocaleDateString('he-IL') : 'לא נמצא',
          citizenship: formattedData.citizenship || 'לא נמצא',
          idNumber: formattedData.idNumber || 'לא נמצא'
        });

        // בדיקה אם יש נתונים שימושיים
        const hasData = formattedData.passportNumber || formattedData.firstNameEn || formattedData.lastNameEn || formattedData.birthDate;
        
        if (hasData) {
          setSuccess(true);
          setTimeout(() => {
            onScanComplete(formattedData);
            onClose();
          }, 1500);
        } else {
          setError('לא ניתן לזהות נתוני דרכון בתמונה. אנא נסה שוב.');
        }
      } else {
        setError(result.error || 'שגיאה בעיבוד התמונה בשרת');
      }

    } catch (err) {
      console.error('❌ שגיאה בעיבוד התמונה:', err);
      setError(`שגיאה בעיבוד התמונה: ${err.message}`);
    } finally {
      setIsScanning(false);
    }
  };

  // extractPassportData function removed - all processing now done server-side with Google Vision API

  const handleClose = () => {
    setError(null);
    setSuccess(false);
    setIsScanning(false);
    setCameraAvailable(false);
    setLastCapturedImage(null);
    setShowDebugImage(false);
    onClose();
  };

  // איפוס המצב כשהמודל נפתח
  React.useEffect(() => {
    if (open) {
      setError(null);
      setSuccess(false);
      setIsScanning(false);
      setCameraAvailable(false);
    }
  }, [open]);

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth={maxWidth}
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: maxHeight,
          height: 'auto'
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {mode === "camera" ? "צילום דרכון" : "העלאת תמונת דרכון"}
          </Typography>
          <IconButton onClick={handleClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ 
          p: 2, 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          textAlign: 'center'
        }}>
          {!isScanning && !success && (
            <>
              {mode === "camera" && (
                <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                כוון את המצלמה לדרכון וצלם תמונה ברורה
              </Typography>
              
              
              {!cameraInitialized && (
                <Typography variant="body2" color="info.main" sx={{ mb: 2 }}>
                  🎥 מאתחל מצלמה...
                </Typography>
              )}

              {cameraInitialized && !cameraAvailable && (
                <Typography variant="body2" color="warning.main" sx={{ mb: 2 }}>
                  ⚠️ מצלמה לא זמינה. אנא:
                  <br />• בדוק שהדפדפן מאפשר גישה למצלמה
                  <br />• רענן את הדף ונסה שוב
                  <br />• השתמש בדפדפן Chrome או Firefox
                  <br />• וודא שהאתר רץ על HTTPS (לא HTTP)
                </Typography>
              )}
              
              {cameraAvailable && (
                <Typography variant="body2" color="success.main" sx={{ mb: 2 }}>
                  ✅ מצלמה זמינה - מוכן לצילום
                </Typography>
              )}
              
              <Box sx={{ 
                border: '2px dashed #ccc', 
                borderRadius: 2, 
                p: 1, 
                mb: 2,
                minHeight: 400,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%'
              }}>
                {cameraInitialized ? (
                  <Webcam
                    ref={webcamRef}
                    audio={false}
                    screenshotFormat="image/jpeg"
                    width="100%"
                    height="auto"
                    style={{ 
                      borderRadius: 8, 
                      width: '100%', 
                      maxWidth: '100%',
                      height: 'auto'
                    }}
                    videoConstraints={{
                      width: { ideal: 1920, min: 800 },
                      height: { ideal: 1440, min: 600 },
                      facingMode: { ideal: "environment" },
                      focusMode: { ideal: "continuous" },
                      exposureMode: { ideal: "continuous" },
                      whiteBalanceMode: { ideal: "continuous" }
                    }}
                    onUserMedia={(stream) => {
                      console.log('🎥 מצלמה נפתחה בהצלחה:', stream);
                      setCameraAvailable(true);
                      setError(null);
                    }}
                    onUserMediaError={(error) => {
                      console.error('❌ שגיאה במצלמה:', error);
                      setCameraAvailable(false);
                      setError(`שגיאה במצלמה: ${error.message || 'לא ניתן לגשת למצלמה'}`);
                    }}
                  />
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <CircularProgress size={40} sx={{ mb: 2 }} />
                    <Typography variant="body2" color="text.secondary">
                      מאתחל מצלמה...
                    </Typography>
                  </Box>
                )}
              </Box>
                </Box>
              )}
              
              {mode === "file" && (
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    בחר תמונה של דרכון מהמכשיר שלך
                  </Typography>
                  
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    style={{ display: 'none' }}
                  />
                  
                  <Button
                    variant="outlined"
                    startIcon={<Upload />}
                    onClick={() => fileInputRef.current?.click()}
                    size="large"
                    sx={{ mb: 2 }}
                  >
                    בחר תמונת דרכון
                  </Button>
                  
                  <Typography variant="body2" color="text.secondary">
                    מומלץ: תמונה ברורה, מוארת היטב, רקע אחיד
                  </Typography>
                </Box>
              )}
            </>
          )}

          {isScanning && (
            <Box sx={{ 
              py: 4, 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center',
              textAlign: 'center'
            }}>
              <CircularProgress size={60} sx={{ mb: 2 }} />
              <Typography variant="h6">מעבד את התמונה...</Typography>
              <Typography variant="body2" color="text.secondary">
                זה יכול לקחת כמה שניות
              </Typography>
            </Box>
          )}

          {success && (
            <Box sx={{ 
              py: 4, 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center',
              textAlign: 'center'
            }}>
              <CheckCircle sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
              <Typography variant="h6" color="success.main">
                סריקה הושלמה בהצלחה!
              </Typography>
              <Typography variant="body2" color="text.secondary">
                הנתונים נטענים לטופס...
              </Typography>
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          {/* הצגת התמונה האחרונה שנצלמה לדיבוג */}
          {showDebugImage && lastCapturedImage && (
            <Box sx={{ mt: 2, border: '1px solid #ccc', borderRadius: 1, overflow: 'hidden' }}>
              <Typography variant="subtitle2" sx={{ p: 1, bgcolor: 'grey.100' }}>
                התמונה שנצלמה לדיבוג:
              </Typography>
              <img
                src={lastCapturedImage}
                alt="תמונה שנצלמה"
                style={{
                  width: '100%',
                  maxHeight: '400px',
                  objectFit: 'contain'
                }}
              />
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} disabled={isScanning}>
          ביטול
        </Button>
        {!isScanning && !success && mode === "camera" && (
          <>
          <Button
            variant="contained"
            startIcon={<CameraAlt />}
            onClick={capture}
              size="large"
              sx={{ mr: 1 }}
              disabled={!cameraAvailable}
            >
              צלם דרכון
            </Button>
            {lastCapturedImage && (
              <Button
                variant="outlined"
                onClick={() => setShowDebugImage(!showDebugImage)}
                size="large"
              >
                {showDebugImage ? 'הסתר תמונה' : 'הצג תמונה אחרונה'}
              </Button>
            )}
          </>
        )}
        
        {!isScanning && !success && mode === "file" && lastCapturedImage && (
          <Button
            variant="outlined"
            onClick={() => setShowDebugImage(!showDebugImage)}
            size="large"
          >
            {showDebugImage ? 'הסתר תמונה' : 'הצג תמונה שהועלתה'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default PassportScanner;
